import React, { useState, useMemo } from 'react';
import { Search, Download, RefreshCw, Filter, Trash2, Activity } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { useLogs, useLogMutations } from '../../hooks/useConvex';
import { useAuth } from '../../contexts/AuthContext';
import { pdfService } from '../../services';

type LogType = 'admin_login' | 'school_login' | 'admin_activity' | 'school_activity';

interface Log {
    log_id: string;
    type: LogType;
    user_email: string;
    action: string;
    details: string;
    timestamp: string;
}

export const LogsPage: React.FC = () => {
    const { user } = useAuth();
    const logsData = useLogs(500);
    const { clearAll } = useLogMutations();
    const loading = logsData === undefined;

    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState<LogType | 'all'>('all');
    const [clearing, setClearing] = useState(false);

    // Transform Convex data to Log type
    const logs = useMemo(() => {
        if (!logsData) return [];
        return logsData.map((log: Record<string, unknown>) => ({
            log_id: log._id as string,
            type: log.type as LogType,
            user_email: log.user_email as string,
            action: log.action as string,
            details: log.details as string,
            timestamp: log.timestamp as string,
        })) as Log[];
    }, [logsData]);

    const filteredLogs = useMemo(() => {
        let filtered = logs;

        if (typeFilter !== 'all') {
            filtered = filtered.filter(log => log.type === typeFilter);
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(log =>
                log.user_email.toLowerCase().includes(query) ||
                log.action.toLowerCase().includes(query) ||
                log.details.toLowerCase().includes(query)
            );
        }

        return filtered;
    }, [searchQuery, typeFilter, logs]);

    const logCounts = useMemo(() => ({
        admin_login: logs.filter(l => l.type === 'admin_login').length,
        school_login: logs.filter(l => l.type === 'school_login').length,
        admin_activity: logs.filter(l => l.type === 'admin_activity').length,
        school_activity: logs.filter(l => l.type === 'school_activity').length,
    }), [logs]);

    const getLogTypeBadge = (type: LogType) => {
        const config: Record<string, 'info' | 'neon' | 'neutral' | 'warning'> = {
            admin_login: 'info',
            school_login: 'neon',
            admin_activity: 'neutral',
            school_activity: 'warning',
        };
        return config[type] || 'neutral';
    };

    const getLogTypeLabel = (type: LogType) => {
        const labels: Record<string, string> = {
            admin_login: 'Admin Login',
            school_login: 'School Login',
            admin_activity: 'Admin Activity',
            school_activity: 'School Activity',
        };
        return labels[type] || type;
    };

    const getActionBadge = (action: string) => {
        if (action.includes('success') || action.includes('create') || action.includes('add')) {
            return 'success';
        }
        if (action.includes('failed') || action.includes('delete') || action.includes('remove')) {
            return 'error';
        }
        if (action.includes('update') || action.includes('edit')) {
            return 'info';
        }
        return 'neutral';
    };

    const handleExportPDF = () => {
        const data = filteredLogs.map(log => [
            new Date(log.timestamp).toLocaleString(),
            getLogTypeLabel(log.type),
            log.user_email,
            log.action,
            log.details
        ]);

        pdfService.generatePDF({
            title: 'System Logs',
            filename: 'system_logs',
            columns: ['Timestamp', 'Type', 'User', 'Action', 'Details'],
            data: data,
            orientation: 'landscape',
        });
    };

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-brand/[0.08] border border-brand/[0.12] flex items-center justify-center">
                            <Activity className="w-5 h-5 text-brand" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white tracking-tight">System Logs</h1>
                            <p className="text-sm text-white/30 mt-0.5">{filteredLogs.length} entries • Real-time monitoring</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.reload()}
                            isLoading={loading}
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </Button>
                        <Button variant="outline" size="sm" onClick={handleExportPDF}>
                            <Download className="w-4 h-4" />
                        </Button>
                        {user?.email === 'chaitanyamishra.ai@gmail.com' && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                    if (window.confirm('Are you sure you want to CLEAR ALL LOGS? This cannot be undone.')) {
                                        try {
                                            setClearing(true);
                                            await clearAll();
                                        } catch (error) {
                                            alert('Failed to clear logs');
                                        } finally {
                                            setClearing(false);
                                        }
                                    }
                                }}
                                isLoading={clearing}
                                className="text-danger/70 hover:text-danger hover:bg-danger/10"
                            >
                                {!clearing && <Trash2 className="w-4 h-4 mr-2" />}
                                Clear All
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card className="p-4 border-t-2 border-t-info">
                    <p className="text-[10px] uppercase tracking-wider text-white/25 mb-2">Admin Logins</p>
                    <p className="text-xl font-mono font-bold text-white">{logCounts.admin_login}</p>
                </Card>
                <Card className="p-4 border-t-2 border-t-purple-500">
                    <p className="text-[10px] uppercase tracking-wider text-white/25 mb-2">School Logins</p>
                    <p className="text-xl font-mono font-bold text-white">{logCounts.school_login}</p>
                </Card>
                <Card className="p-4 border-t-2 border-t-brand">
                    <p className="text-[10px] uppercase tracking-wider text-white/25 mb-2">Admin Activity</p>
                    <p className="text-xl font-mono font-bold text-white">{logCounts.admin_activity}</p>
                </Card>
                <Card className="p-4 border-t-2 border-t-emerald-500">
                    <p className="text-[10px] uppercase tracking-wider text-white/25 mb-2">School Activity</p>
                    <p className="text-xl font-mono font-bold text-white">{logCounts.school_activity}</p>
                </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-1">
                    <Input
                        placeholder="Search by user, action, or details..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        leftIcon={<Search className="w-5 h-5 text-white/30" />}
                        fullWidth
                    />
                </div>
                <div className="w-full md:w-48">
                    <div className="relative">
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value as LogType | 'all')}
                            className="w-full px-4 py-3 rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.06] text-white focus:border-brand/50 focus:outline-none appearance-none cursor-pointer"
                        >
                            <option value="all" className="bg-[#0A0A0A]">All Types</option>
                            <option value="admin_login" className="bg-[#0A0A0A]">Admin Logins</option>
                            <option value="school_login" className="bg-[#0A0A0A]">School Logins</option>
                            <option value="admin_activity" className="bg-[#0A0A0A]">Admin Activities</option>
                            <option value="school_activity" className="bg-[#0A0A0A]">School Activities</option>
                        </select>
                        <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                    </div>
                </div>
            </div>

            {/* Logs Table */}
            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-white/[0.02]">
                            <tr>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Time</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Type</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">User</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Action</th>
                                <th className="px-6 py-3.5 text-left text-[11px] font-medium uppercase tracking-wider text-white/25">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.03]">
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center">
                                        <Loader size="md" className="mx-auto" />
                                    </td>
                                </tr>
                            ) : filteredLogs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-6 py-16 text-center text-white/30">
                                        No logs found matching your criteria
                                    </td>
                                </tr>
                            ) : (
                                filteredLogs.map((log) => (
                                    <tr key={log.log_id} className="hover:bg-white/[0.02] transition-colors duration-150">
                                        <td className="px-6 py-4 text-[11px] text-white/25 whitespace-nowrap font-mono">
                                            {new Date(log.timestamp).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <Badge variant={getLogTypeBadge(log.type)} size="sm">
                                                {getLogTypeLabel(log.type)}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-white/70 font-mono whitespace-nowrap">
                                            {log.user_email}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <Badge variant={getActionBadge(log.action)} size="sm">
                                                {log.action.replace(/_/g, ' ')}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-white/40 max-w-md truncate" title={log.details}>
                                            {log.details}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>
        </div>
    );
};
